import type {
    ActionMetrics,
    MetricsCustomData,
    Opt,
    ActionModel,
    MetricsFields,
} from '@jet/environment';
import { createMetricsClickData } from '@jet/environment/metrics/builder';
import {
    inject,
    ObjectGraph,
} from '@jet/environment/dependencies/object-graph';
import { MetricsClickTargetType } from '@jet-app/music-classical/metrics/click';
import { ObjectGraphType } from '@jet-app/music-classical/dependencies';
import type {
    ContentKind,
    NativeApps,
    Page,
} from '@jet-app/music-classical/models';
import type { Intent } from '@jet/environment/dispatching';
import type { IntentKind } from '../intents';

export declare type InvalidationActionEventScope = 'global' | 'page' | 'inapp';
export declare type InvalidationActionKind =
    | 'expirationDate'
    | 'playActivity'
    | 'nextPage'
    | 'subscriptionStatusChange'
    | 'libraryItemsDeleted'
    | 'libraryItemAdded'
    | 'selectedArtistIdChanged'
    | 'sortByChanged'
    | 'favoritesPlaylistStatusChanged'
    | 'playlistHeaderChanged'
    | 'suggestionAdded'
    | 'refreshSuggestions'
    | 'libraryItemsReordered'
    | 'loadingCompleted'
    | 'recentCatalogSearchesChanged'
    | 'libraryPlaylistUpdated'; // Add any other types if we create new ones;

export interface InvalidationActionIntentMetadata {
    [key: string]: string | number | boolean | null;
}

export interface InvalidationAction extends ActionModel {
    metadata?: Opt<InvalidationActionIntentMetadata>;
    scope: InvalidationActionEventScope;
    kind: InvalidationActionKind;
}

export interface ExternalUrlAction extends ActionModel {
    readonly $kind: 'openExternalURLAction';
    readonly url: string;
}

export interface FlowAction extends ActionModel {
    readonly $kind: 'flowAction';
    readonly destination: FlowActionDestination;
    readonly url?: string;
}

export interface LaunchNativeAppAction extends ActionModel {
    readonly $kind: 'LaunchNativeAppAction';
    readonly url?: string;
    readonly appToOpen: NativeApps;
}

export const isFlowAction = (
    action: ActionModel | null,
): action is FlowAction => {
    return action?.$kind === 'flowAction';
};

interface ContentDescriptor {
    kind?: ContentKind;
    url?: Opt<string>;
    catalogUrl?: Opt<string>;
}

/**
 * A union type of all the possible destinations for FlowAction.
 */
export type FlowActionDestination =
    | Intent<Page>
    | {
          readonly kind: IntentKind;
          readonly contentDescriptor: ContentDescriptor;
      };

/**
 * Creates a FlowAction For a given destination.
 * @param destination Destination of flow action.
 * @param actionMetrics Metrics related to the action.
 */
export const makeFlowAction = (
    destination: FlowActionDestination,
    actionMetrics: ActionMetrics,
): FlowAction => {
    return {
        $kind: 'flowAction',
        destination,
        actionMetrics,
    };
};

/**
 * Creates a ExternalUrlAction For a given URL.
 * @param url action URL
 * @param actionMetrics Metrics related to the action.
 */
export const makeExternalUrlAction = (
    url: string,
    actionMetrics: ActionMetrics,
): ExternalUrlAction => {
    return {
        $kind: 'openExternalURLAction',
        url,
        actionMetrics,
    };
};

/**
 * Creates a LaunchNativeAppAction for a given URL.
 * @param url action URL
 * @param appType app to link out to
 */
export const makeLaunchNativeAppAction = (
    url: string,
    appType?: NativeApps,
): LaunchNativeAppAction => {
    const appToOpen = appType ?? null;
    return {
        $kind: 'LaunchNativeAppAction',
        url,
        actionMetrics: { data: [], custom: {} },
        appToOpen,
    };
};

export const createActionMetrics = (
    objectGraph: ObjectGraph,
    targetId: string,
    targetType: MetricsClickTargetType,
    actionType: string,
    eventFields?: MetricsFields,
    customData?: Opt<MetricsCustomData>,
): ActionMetrics => {
    // Custom data is unused unless a custom fields provider is created on the native side, so we need to add
    // it to event fields to report recoId on clicks
    eventFields = { ...eventFields, ...customData };
    eventFields['actionType'] = actionType;

    const metrics = inject(ObjectGraphType.metrics, objectGraph);
    const clickMetrics = createMetricsClickData(
        targetId,
        targetType,
        eventFields,
        metrics.configuration,
    );
    clickMetrics.fields.eventVersion = 5;

    return {
        data: [clickMetrics],
        custom: customData ?? {},
    };
};
